import React, { useState } from 'react';
import { db, auth } from '../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import GrowthChart from './GrowthChart';
import { formatLetterForAssessment, letterCaseOf, hasLetterCase } from '../utils/letterCase';
import { caseTagText, caseTagTitle } from '../utils/assessmentEntry';
import { buildResponseCellTooltip } from '../utils/incorrectResponses';
import { auditResultChange, resultSnapshot, RESULT_AUDIT_ACTIONS } from '../utils/resultAudit';
import {
  VOWEL_TEST_LABELS,
  RESP_NOT_ADMINISTERED,
  isVowelTestType,
  vowelItemKey,
  vowelItemLabel,
  enabledVowelTestTypes,
} from '../constants/vowelTests';

// The five student-facing vowels — rows of the optional vowel table below.
const VOWEL_ROWS = ['a', 'e', 'i', 'o', 'u'];

// The small case tag in a date header (v3.85.0): Aa / aa / A a, only for docs
// that carry `letterCase` — older docs render exactly as before.
const CASE_TAG_STYLE = { marginLeft: '4px', padding: '0 4px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#f8fafc', color: '#475569', fontSize: '0.8em', fontWeight: 600, whiteSpace: 'nowrap' };
const CaseTag = ({ assessment }) => (hasLetterCase(assessment)
    ? <span className="case-tag" title={caseTagTitle(assessment.letterCase)} style={CASE_TAG_STYLE}>{caseTagText(assessment.letterCase)}</span>
    : null);
// A letter the run preset as not administered (v3.85.0, a consonants-only
// Sounds run: a e i o u) — the vowel table's grey "n/a" cell, reused.
const isNotAdministeredLetter = (assessment, letter) =>
    !!assessment && Array.isArray(assessment.notAdministered)
    && assessment.notAdministered.some(l => String(l).toLowerCase() === String(letter).toLowerCase());

// Milliseconds of an attempt's timestamp — a JS Date (StudentReport / Results
// convert), a Firestore Timestamp, or a raw { seconds } object. No usable
// timestamp counts as oldest, so a dated attempt always wins over it.
const attemptTime = (assessment) => {
    const t = assessment && assessment.timestamp;
    if (!t) return 0;
    if (typeof t.toMillis === 'function') return t.toMillis();
    if (typeof t.toDate === 'function') return t.toDate().getTime();
    if (typeof t.seconds === 'number') return t.seconds * 1000;
    const ms = new Date(t).getTime();
    return Number.isNaN(ms) ? 0 : ms;
};

function StudentResultTable({ student, assessments, letterColumns: preferredLetterColumns, onRefresh, user }) {
    const [editConfig, setEditConfig] = useState(null); // { testType, quarter, letter, assessment }
    const [showGrowthChart, setShowGrowthChart] = useState(true);

    if (!student) return null;

    const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
    const letterColumns = (preferredLetterColumns && preferredLetterColumns.length > 0) 
        ? preferredLetterColumns.map(l => l.toUpperCase()) 
        : ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];

    // The record shown for a test + quarter: the LATEST attempt (v3.92.1).
    // Re-assessing saves a new doc, and the query returns docs in no useful
    // order, so the old `.find()` showed whichever attempt came first — an
    // older score/date than the class Results grid (Elmbrook K report, Oct
    // 13). Same pick as the Results summary grid: latest timestamp wins, an
    // N/A doc counts as an attempt, a tie keeps the first seen. One
    // difference: an undated doc counts as oldest here (in the grid it is
    // never replaced once seen). Detailed Letter Responses, the Class Report
    // and NextPath skip N/A docs, so when a slot's latest doc is N/A they
    // show the previous scored attempt while this table (like the grid)
    // shows the blank N/A slot.
    const getAssessment = (testType, quarter) => {
        let assessment;
        assessments.forEach(a => {
            if (a.studentId !== student.studentId || a.testType !== testType) return;
            if (!a.quarter || !a.quarter.includes(quarter)) return;
            if (!assessment || attemptTime(a) > attemptTime(assessment)) assessment = a;
        });
        if (assessment && assessment.manualEntry) {
            console.log(`Manual entry found for ${testType} ${quarter}:`, assessment);
        }
        return assessment;
    };

    // The case the Sounds letter column is drawn in (v3.85.0): the latest
    // Sounds run of the year that carries `letterCase` (Q4 first); none →
    // uppercase, exactly as before. A both-case run shows "M m".
    const soundsLabelDoc = [...quarters].reverse().map(q => getAssessment('sounds', q)).find(a => a && hasLetterCase(a)) || null;
    const soundsLabelCase = soundsLabelDoc ? letterCaseOf(soundsLabelDoc) : 'uppercase';

    const formatDate = (assessment) => {
        if (!assessment) return '-';
        if (assessment.manualEntry) return 'Manual';
        
        let dateStr = '-';
        if (assessment.timestamp) {
            const date = assessment.timestamp.toDate ? assessment.timestamp.toDate() : new Date(assessment.timestamp);
            dateStr = date.toLocaleDateString();
        }

        if (assessment.totalDurationMs) {
            const totalSeconds = Math.floor(assessment.totalDurationMs / 1000);
            const mins = Math.floor(totalSeconds / 60);
            const secs = totalSeconds % 60;
            const timeStr = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
            return (
                <span>
                    {dateStr}<br/>
                    <span style={{ fontSize: '0.85em', color: '#888' }}>⏱️ {timeStr}</span>
                </span>
            );
        }
        
        return dateStr;
    };

    const getFluencyData = (assessment, letter) => {
        if (!assessment || !assessment.letterFluency) return null;
        return assessment.letterFluency[letter] ||
               assessment.letterFluency[letter.toUpperCase()] ||
               assessment.letterFluency[letter.toLowerCase()] || null;
    };

    // What the student actually said, captured on incorrect responses via the
    // letter-key shortcut (v3.27.0). Same case-insensitive lookup as fluency.
    const getHeardLetter = (assessment, letter) => {
        if (!assessment || !assessment.incorrectResponses) return null;
        return assessment.incorrectResponses[letter] ||
               assessment.incorrectResponses[letter.toUpperCase()] ||
               assessment.incorrectResponses[letter.toLowerCase()] || null;
    };

    const checkResponse = (assessment, letter) => {
        if (!assessment) return '';

        let response = '';
        if (assessment.responses) {
            response = assessment.responses[letter] ||
                assessment.responses[letter.toUpperCase()] ||
                assessment.responses[letter.toLowerCase()];
        }

        if (!response) {
            if (assessment.manualEntry && assessment.correctCount === 0) return '✗';
            return '';
        }

        return response === 'Correct' ? '✓' : response === 'Incorrect' ? '✗' : '';
    };

    const getCellColor = (res) => {
        if (res === '✓') return '#90ee90';
        if (res === '✗') return '#ff6347';
        return 'transparent';
    };

    // ── Optional vowel tests (v3.73.0) ─────────────────────────────────────
    // Rendered as a second, five-row table under the letter table, only when
    // this student has at least one vowel doc this year (so 4K report cards
    // and printouts are byte-identical to before) and the kill switch is on.
    const vowelTypes = enabledVowelTestTypes();
    const hasVowelData = vowelTypes.length > 0
        && assessments.some(a => a.studentId === student.studentId && isVowelTestType(a.testType));

    // Vowel cells carry a third state: not administered (gray "n/a"), never
    // counted as incorrect. Manual-entry docs (responses:{}) show nothing per
    // item — the Total row still shows their correctCount.
    const vowelResponse = (assessment, testType, vowel) => {
        if (!assessment || !assessment.responses) return '';
        const r = assessment.responses[vowelItemKey(testType, vowel)];
        if (r === 'Correct') return '✓';
        if (r === 'Incorrect') return '✗';
        if (r === RESP_NOT_ADMINISTERED) return 'n/a';
        return '';
    };

    const vowelTotal = (testType, quarter) => {
        const assessment = getAssessment(testType, quarter);
        if (!assessment) return 0;
        const hasDetailedResponses = assessment.responses && Object.keys(assessment.responses).length > 0;
        if (!hasDetailedResponses) return assessment.correctCount > 0 ? assessment.correctCount : 0;
        return VOWEL_ROWS.filter(v => vowelResponse(assessment, testType, v) === '✓').length;
    };

    const calculateTotal = (testType, quarter) => {
        const assessment = getAssessment(testType, quarter);
        if (!assessment) return 0;

        // Manual-entry assessments save responses:{} with the score in correctCount.
        // Fall back to correctCount when there are no per-letter responses to count.
        const hasDetailedResponses = assessment.responses && Object.keys(assessment.responses).length > 0;
        if (!hasDetailedResponses) {
            return assessment.correctCount > 0 ? assessment.correctCount : 0;
        }

        let total = 0;
        letterColumns.forEach(letter => {
            const res = checkResponse(assessment, letter);
            if (res === '✓') {
                total++;
            }
        });
        return total;
    };

    const handleCellDoubleClick = (testType, quarter, letter) => {
        const assessment = getAssessment(testType, quarter);

        // 1. Check if assessment exists (must have at least a manual one)
        if (!assessment) {
            alert("No assessment record exists for this quarter. Please use the main table to create a backdated score first.");
            return;
        }

        // 2. Open Custom Modal
        setEditConfig({ testType, quarter, letter, assessment });
    };

    const handleSaveEdit = async (newResponse) => {
        if (!editConfig) return;

        const { letter, assessment } = editConfig;

        try {
            // 3. Update Firestore
            const assessmentRef = doc(db, 'assessments', assessment.id);
            const currentResponses = assessment.responses || {};

            // Handle key mapping (store lowercase keyed letters generally, but follow existing pattern)
            const updatedResponses = { ...currentResponses };
            if (newResponse) {
                updatedResponses[letter] = newResponse;
            } else {
                delete updatedResponses[letter];
            }

            // 4. Calculate new total from detailed responses
            const calculatedTotal = Object.values(updatedResponses).filter(r => r === 'Correct').length;

            // 5. Conflict Resolution
            let finalCorrectCount = assessment.correctCount;
            if (calculatedTotal !== assessment.correctCount) {
                const userConfirmed = window.confirm(
                    `Conflict Detected:\n\nThe calculated score from detailed letters is ${calculatedTotal}.\nThe saved total score is ${assessment.correctCount}.\n\nDo you want to update the saved total score to match the detailed calculation (${calculatedTotal})?`
                );
                if (userConfirmed) {
                    finalCorrectCount = calculatedTotal;
                }
            }

            await updateDoc(assessmentRef, {
                responses: updatedResponses,
                correctCount: finalCorrectCount
            });
            // v3.98.1: the correction is on record — who, when, which letter,
            // the answer and score before and after (resultAudit.js). The app
            // user when the caller passes one (the student pop-up does), else
            // the signed-in Firebase user (the /student page).
            const actor = user || (auth ? auth.currentUser : null);
            const previousKey = Object.keys(currentResponses).find((k) => k.toLowerCase() === String(letter).toLowerCase());
            await auditResultChange(actor, RESULT_AUDIT_ACTIONS.ITEM_EDITED, {
                id: assessment.id,
                studentId: assessment.studentId,
                classId: assessment.classId,
                testType: assessment.testType || editConfig.testType,
                quarter: assessment.quarter,
            }, {
                before: resultSnapshot(assessment),
                after: resultSnapshot({ ...assessment, responses: updatedResponses, correctCount: finalCorrectCount }),
                item: { key: letter, before: previousKey !== undefined ? currentResponses[previousKey] : null, after: newResponse || null },
            });

            // 6. Refresh Data & Close Modal
            setEditConfig(null);
            if (onRefresh) onRefresh();

        } catch (err) {
            console.error("Error updating cell:", err);
            alert("Failed to update result.");
        }
    };

    return (
        <div className="table-container">
            <div className="no-print" style={{ marginBottom: '15px', display: 'flex', justifyContent: 'flex-end' }}>
                <label className="checkbox-row" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', fontSize: '0.95rem', color: '#555' }}>
                    <input
                        type="checkbox"
                        checked={showGrowthChart}
                        onChange={(e) => setShowGrowthChart(e.target.checked)}
                        style={{ marginRight: '8px', cursor: 'pointer', transform: 'scale(1.2)' }}
                    />
                    Show Growth Chart
                </label>
            </div>

            {showGrowthChart && (
                <div className="no-print">
                    <GrowthChart assessments={assessments} />
                </div>
            )}

            <table className="student-details-table" style={{ marginTop: '20px' }}>
                <thead>
                    <tr>
                        <th rowSpan="2" className="thick-border-left thick-border-top" style={{ verticalAlign: 'middle' }}>Letter</th>
                        <th colSpan={4} className="thick-border-top thick-border-right">Letter Names: Uppercase</th>
                        <th rowSpan="2" className="thick-border-left thick-border-top" style={{ verticalAlign: 'middle' }}>Letter</th>
                        <th colSpan={4} className="thick-border-top thick-border-right">Letter Names: Lowercase</th>
                        <th rowSpan="2" className="thick-border-left thick-border-top" style={{ verticalAlign: 'middle' }}>Letter</th>
                        <th colSpan={4} className="thick-border-top thick-border-right">Letter: Sounds</th>
                    </tr>
                    <tr className="date-row">
                        {/* Uppercase Dates */}
                        {quarters.map((q, i) => {
                            const assessment = getAssessment('uppercase', q);
                            return <th key={`uc-${q}`} className={i === 3 ? 'thick-border-right' : ''}>{q} <br /><span className="date-sub">{formatDate(assessment)}</span></th>;
                        })}
                        {/* Lowercase Dates */}
                        {quarters.map((q, i) => {
                            const assessment = getAssessment('lowercase', q);
                            return <th key={`lc-${q}`} className={i === 3 ? 'thick-border-right' : ''}>{q} <br /><span className="date-sub">{formatDate(assessment)}</span></th>;
                        })}
                        {/* Sounds Dates (+ the case tag for a run that recorded its case) */}
                        {quarters.map((q, i) => {
                            const assessment = getAssessment('sounds', q);
                            return <th key={`snd-${q}`} className={i === 3 ? 'thick-border-right' : ''}>{q}<CaseTag assessment={assessment} /> <br /><span className="date-sub">{formatDate(assessment)}</span></th>;
                        })}
                    </tr>
                </thead>
                <tbody>
                    {letterColumns.map(letter => (
                        <tr key={letter}>
                            <td className="thick-border-left" style={{ fontWeight: 'bold', fontSize: '1.5rem' }}>
                                {/* Uppercase Column: Show dash for lowercase-only variants */}
                                {letter === 'a_hand' ? <span style={{ fontFamily: '"Times New Roman", Times, serif', fontSize: '1.2em' }}>a</span> :
                                    letter === 'g_fancy' ? <span style={{ fontFamily: '"Times New Roman", Times, serif', fontSize: '1.2em' }}>g</span> :
                                        letter.length > 1 ? '-' : formatLetterForAssessment('uppercase', letter)}
                            </td>
                            {/* Uppercase Results */}
                            {quarters.map((q, i) => {
                                if (letter.length > 1) return <td key={`uc-${letter}-${q}`} className={i === 3 ? 'thick-border-right' : ''} style={{ backgroundColor: '#eee' }}></td>;
                                const assessment = getAssessment('uppercase', q);
                                const isNA = assessment && assessment.correctCount === -1;
                                const res = checkResponse(assessment, letter);
                                const fluencyMs = getFluencyData(assessment, letter);
                                const isStruggling = fluencyMs && fluencyMs >= 3000;
                                const cellTitle = buildResponseCellTooltip(res === '✓' ? 'Correct' : res === '✗' ? 'Incorrect' : undefined, getHeardLetter(assessment, letter), fluencyMs);
                                const tooltipProps = cellTitle ? { title: cellTitle } : {};
                                
                                return <td key={`uc-${letter}-${q}`} {...tooltipProps} onDoubleClick={() => !isNA && handleCellDoubleClick('uppercase', q, letter)} className={`${i === 3 ? 'thick-border-right' : ''} ${isNA ? 'score-na' : ''}`} style={{ backgroundColor: isNA ? '' : getCellColor(res), cursor: isNA ? 'not-allowed' : 'pointer', borderBottom: isStruggling && res ? '3px solid orange' : '' }}>{isNA ? '' : res}</td>;
                            })}
                            <td className="thick-border-left" style={{ fontWeight: 'bold', fontSize: '1.5rem' }}>
                                {/* Handle display for variants vs standard letters */}
                                {letter === 'a_hand' ? <span style={{ fontFamily: '"Comic Sans MS", "Chalkboard SE", sans-serif' }}>a</span> :
                                    letter === 'g_fancy' ? <span style={{ fontFamily: '"Times New Roman", Times, serif', fontSize: '1.2em' }}>g</span> :
                                        formatLetterForAssessment('lowercase', letter)}
                            </td>
                            {/* Lowercase Results */}
                            {quarters.map((q, i) => {
                                // For variants, pass the special key directly. For others, just use the letter.
                                const searchKey = letter;
                                const assessment = getAssessment('lowercase', q);
                                const isNA = assessment && assessment.correctCount === -1;
                                const res = checkResponse(assessment, searchKey);
                                const fluencyMs = getFluencyData(assessment, searchKey);
                                const isStruggling = fluencyMs && fluencyMs >= 3000;
                                const cellTitle = buildResponseCellTooltip(res === '✓' ? 'Correct' : res === '✗' ? 'Incorrect' : undefined, getHeardLetter(assessment, letter), fluencyMs);
                                const tooltipProps = cellTitle ? { title: cellTitle } : {};
                                
                                return <td key={`lc-${letter}-${q}`} {...tooltipProps} onDoubleClick={() => !isNA && handleCellDoubleClick('lowercase', q, searchKey)} className={`${i === 3 ? 'thick-border-right' : ''} ${isNA ? 'score-na' : ''}`} style={{ backgroundColor: isNA ? '' : getCellColor(res), cursor: isNA ? 'not-allowed' : 'pointer', borderBottom: isStruggling && res ? '3px solid orange' : '' }}>{isNA ? '' : res}</td>;
                            })}
                            <td className="thick-border-left" style={{ fontWeight: 'bold', fontSize: '1.5rem', whiteSpace: 'nowrap' }}>
                                {/* Sounds column: the letter in the case the latest Sounds run was
                                    given in (uppercase for every run before v3.85.0); dash for
                                    lowercase-only variants */}
                                {letter === 'a_hand' ? <span style={{ fontFamily: '"Times New Roman", Times, serif', fontSize: '1.2em' }}>a</span> :
                                    letter === 'g_fancy' ? <span style={{ fontFamily: '"Times New Roman", Times, serif', fontSize: '1.2em' }}>g</span> :
                                        letter.length > 1 ? '-' : formatLetterForAssessment({ testType: 'sounds', letterCase: soundsLabelCase }, letter)}
                            </td>
                            {/* Sounds Results - Empty for variants */}
                            {quarters.map((q, i) => {
                                if (letter.length > 1) return <td key={`snd-${letter}-${q}`} className={i === 3 ? 'thick-border-right' : ''} style={{ backgroundColor: '#eee' }}></td>;
                                const assessment = getAssessment('sounds', q);
                                const isNA = assessment && assessment.correctCount === -1;
                                // v3.85.0: a letter the run preset as not administered → grey "n/a", no edit.
                                if (!isNA && isNotAdministeredLetter(assessment, letter)) {
                                    return <td key={`snd-${letter}-${q}`} title="Not administered" className={i === 3 ? 'thick-border-right' : ''} style={{ backgroundColor: '#e5e7eb', color: '#6b7280', fontSize: '0.85em', cursor: 'default' }}>n/a</td>;
                                }
                                const res = checkResponse(assessment, letter);
                                const fluencyMs = getFluencyData(assessment, letter);
                                const isStruggling = fluencyMs && fluencyMs >= 3000;
                                const cellTitle = buildResponseCellTooltip(res === '✓' ? 'Correct' : res === '✗' ? 'Incorrect' : undefined, getHeardLetter(assessment, letter), fluencyMs);
                                const tooltipProps = cellTitle ? { title: cellTitle } : {};
                                
                                return <td key={`snd-${letter}-${q}`} {...tooltipProps} onDoubleClick={() => !isNA && handleCellDoubleClick('sounds', q, letter)} className={`${i === 3 ? 'thick-border-right' : ''} ${isNA ? 'score-na' : ''}`} style={{ backgroundColor: isNA ? '' : getCellColor(res), cursor: isNA ? 'not-allowed' : 'pointer', borderBottom: isStruggling && res ? '3px solid orange' : '' }}>{isNA ? '' : res}</td>;
                            })}
                        </tr>
                    ))}
                    {/* Total Row moved to tbody to avoid print layout issues with tfoot */}
                    <tr className="total-row" style={{ fontWeight: 'bold' }}>
                        <td className="thick-border-left thick-border-bottom">Total</td>
                        {/* Uppercase Totals */}
                        {quarters.map((q, i) => {
                            const assessment = getAssessment('uppercase', q);
                            const isNA = assessment && assessment.correctCount === -1;
                            return <td key={`total-uc-${q}`} className={`thick-border-bottom ${i === 3 ? 'thick-border-right' : ''} ${isNA ? 'score-na' : ''}`}>{isNA ? '' : calculateTotal('uppercase', q)}</td>;
                        })}
                        <td className="thick-border-left thick-border-bottom">Total</td>
                        {/* Lowercase Totals */}
                        {quarters.map((q, i) => {
                            const assessment = getAssessment('lowercase', q);
                            const isNA = assessment && assessment.correctCount === -1;
                            return <td key={`total-lc-${q}`} className={`thick-border-bottom ${i === 3 ? 'thick-border-right' : ''} ${isNA ? 'score-na' : ''}`}>{isNA ? '' : calculateTotal('lowercase', q)}</td>;
                        })}
                        <td className="thick-border-left thick-border-bottom">Total</td>
                        {/* Sounds Totals */}
                        {quarters.map((q, i) => {
                            const assessment = getAssessment('sounds', q);
                            const isNA = assessment && assessment.correctCount === -1;
                            return <td key={`total-snd-${q}`} className={`thick-border-bottom ${i === 3 ? 'thick-border-right' : ''} ${isNA ? 'score-na' : ''}`}>{isNA ? '' : calculateTotal('sounds', q)}</td>;
                        })}
                    </tr>
                </tbody>
            </table>

            {hasVowelData && (
                <table className="student-details-table student-vowel-table" style={{ marginTop: '14px' }}>
                    <thead>
                        <tr>
                            <th rowSpan="2" className="thick-border-left thick-border-top" style={{ verticalAlign: 'middle' }}>Vowel</th>
                            {vowelTypes.map(vt => (
                                <th key={vt} colSpan={4} className="thick-border-top thick-border-right">Vowels: {VOWEL_TEST_LABELS[vt].replace(' Vowels', '')}</th>
                            ))}
                        </tr>
                        <tr className="date-row">
                            {vowelTypes.map(vt => quarters.map((q, i) => {
                                const assessment = getAssessment(vt, q);
                                return <th key={`${vt}-${q}`} className={i === 3 ? 'thick-border-right' : ''}>{q}<CaseTag assessment={assessment} /> <br /><span className="date-sub">{formatDate(assessment)}</span></th>;
                            }))}
                        </tr>
                    </thead>
                    <tbody>
                        {VOWEL_ROWS.map(vowel => (
                            <tr key={vowel}>
                                <td className="thick-border-left" style={{ fontWeight: 'bold', fontSize: '1.5rem' }}>{vowel}</td>
                                {vowelTypes.map(vt => quarters.map((q, i) => {
                                    const assessment = getAssessment(vt, q);
                                    const isNA = assessment && assessment.correctCount === -1;
                                    const res = vowelResponse(assessment, vt, vowel);
                                    const itemKey = vowelItemKey(vt, vowel);
                                    const fluencyMs = getFluencyData(assessment, itemKey);
                                    const isStruggling = fluencyMs && fluencyMs >= 3000;
                                    const cellTitle = res === 'n/a'
                                        ? 'Not administered'
                                        : buildResponseCellTooltip(res === '✓' ? 'Correct' : res === '✗' ? 'Incorrect' : undefined, getHeardLetter(assessment, itemKey), fluencyMs);
                                    const tooltipProps = cellTitle ? { title: cellTitle } : {};
                                    const notAdministered = res === 'n/a';
                                    return (
                                        <td
                                            key={`${vt}-${vowel}-${q}`}
                                            {...tooltipProps}
                                            onDoubleClick={() => !isNA && handleCellDoubleClick(vt, q, itemKey)}
                                            className={`${i === 3 ? 'thick-border-right' : ''} ${isNA ? 'score-na' : ''}`}
                                            style={{
                                                backgroundColor: isNA ? '' : notAdministered ? '#e5e7eb' : getCellColor(res),
                                                color: notAdministered ? '#6b7280' : 'inherit',
                                                fontSize: notAdministered ? '0.85em' : undefined,
                                                cursor: isNA ? 'not-allowed' : 'pointer',
                                                borderBottom: isStruggling && res && !notAdministered ? '3px solid orange' : ''
                                            }}
                                        >
                                            {isNA ? '' : res}
                                        </td>
                                    );
                                }))}
                            </tr>
                        ))}
                        <tr className="total-row" style={{ fontWeight: 'bold' }}>
                            <td className="thick-border-left thick-border-bottom">Total</td>
                            {vowelTypes.map(vt => quarters.map((q, i) => {
                                const assessment = getAssessment(vt, q);
                                const isNA = assessment && assessment.correctCount === -1;
                                return <td key={`total-${vt}-${q}`} className={`thick-border-bottom ${i === 3 ? 'thick-border-right' : ''} ${isNA ? 'score-na' : ''}`}>{isNA ? '' : vowelTotal(vt, q)}</td>;
                            }))}
                        </tr>
                    </tbody>
                </table>
            )}
            <style>{`
        .student-details-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 15px;
          font-family: 'Comic Sans MS', 'Comic Sans', 'Chalkboard SE', 'Comic Neue', sans-serif; /* Friendly Font */
        }
        .student-details-table th, .student-details-table td {
          border: 1px solid #888;
          padding: 8px;
          text-align: center;
          font-size: 1.1rem; /* Larger letters as requested */
        }
        .student-details-table th {
            background-color: #f4f4f4;
        }
        .date-row th {
            font-weight: normal;
            font-size: 0.9em;
        }
        .date-sub {
            font-size: 0.8em;
            color: #666;
        }
        .thick-border-left { border-left: 3px solid #000 !important; }
        .thick-border-right { border-right: 3px solid #000 !important; }
        .thick-border-top { border-top: 3px solid #000 !important; }
        .thick-border-bottom { border-bottom: 3px solid #000 !important; }

            @media print {
                .student-details-table {
                    width: 100%;
                    /* fixed layout bounds the table's minimum width to the page,
                       so content can never trigger the browser's shrink-to-fit
                       print scaling (v3.32.1 — reports printed small) */
                    table-layout: fixed;
                    border-collapse: collapse;
                    margin-top: 0 !important; /* RECOVER VERTICAL SPACE */
                }
                .student-details-table th, .student-details-table td {
                    padding: 2px 2px !important; /* EXTREME COMPRESSION for variants */
                    font-size: 9pt !important;   /* Reduce font size */
                    line-height: 1.1 !important; /* Tightest possible line height */
                    border: 1px solid #333 !important; /* Visible internal grid lines */
                    white-space: normal !important;
                    overflow-wrap: break-word;   /* wrap, never widen the page */
                }
                .student-details-table thead th {
                    padding: 4px 2px !important;
                    line-height: 1.2 !important;
                }
                .date-row th, .date-sub {
                    font-size: 8pt;
                }
                 .thick-border-left { border-left: 2px solid #000 !important; }
                 .thick-border-right { border-right: 2px solid #000 !important; }
                 .thick-border-top { border-top: 2px solid #000 !important; }
                 .thick-border-bottom { border-bottom: 2px solid #000 !important; }
                 
                 /* Force Total Row (Cell-Based Strategy) */
                 .total-row {
                     height: auto !important;
                     /* Remove TR-level styles preventing cell rendering */
                     background-color: transparent !important;
                     border: none !important;
                 }
                 .total-row td {
                     display: table-cell !important;
                     height: auto !important;
                     padding: 4px !important; /* Compact to ensure fit */
                     color: black !important;
                     font-weight: bold !important;
                     visibility: visible !important;
                     background-color: #e6e6e6 !important; /* Move background here */
                     -webkit-print-color-adjust: exact !important;
                     print-color-adjust: exact !important;
                     border: 1px solid #444 !important; /* Ensure grid lines exist */
                     border-bottom: 2px solid #000 !important; /* Restore thick bottom */
                 }
            }
      `}</style>
            {editConfig && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                    <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', minWidth: '300px', textAlign: 'center' }}>
                        <h3>Update Result</h3>
                        <p>Letter: <strong>{isVowelTestType(editConfig.testType) ? vowelItemLabel(editConfig.letter) : formatLetterForAssessment(editConfig.testType, editConfig.letter)}</strong> ({editConfig.quarter})</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '20px' }}>
                            <button onClick={() => handleSaveEdit('Correct')} style={{ padding: '10px', backgroundColor: '#90ee90', border: '1px solid #ccc', cursor: 'pointer', fontSize: '1.2em' }}>Correct (C)</button>
                            <button onClick={() => handleSaveEdit('Incorrect')} style={{ padding: '10px', backgroundColor: '#ff6347', border: '1px solid #ccc', cursor: 'pointer', fontSize: '1.2em' }}>Incorrect (I)</button>
                            <button onClick={() => handleSaveEdit(null)} style={{ padding: '10px', backgroundColor: '#f4f4f4', border: '1px solid #ccc', cursor: 'pointer' }}>Clear Result</button>
                        </div>
                        <button onClick={() => setEditConfig(null)} style={{ marginTop: '20px', padding: '5px 10px', cursor: 'pointer' }}>Cancel</button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default StudentResultTable;
